/**
 * The clock between inspecting a site and getting the report into the CER
 * portal.
 *
 * Leading Edge has **10 days from the inspection date to upload**. That deadline is not
 * ours — it is the commitment the CER-engaged inspection companies hold — and
 * missing it is the kind of failure that costs the tranche, not just the job.
 * The dashboard should therefore be loud about it well before it lands, so
 * the alert threshold sits at **7 days**: enough runway to chase a photo dump,
 * re-book a return visit, or push a report through QA.
 *
 * MEASURED FROM THE INSPECTION DATE, NOT FROM SUBMISSION
 * ------------------------------------------
 * The clock starts on the inspection date — the day the inspector was on site. Report `submittedAt` is
 * a different and sometimes wildly later thing — a legacy Dropbox import
 * submits weeks after the visit, and a supervisor edit re-stamps it again —
 * so measuring from it would report a comfortable deadline on a report that
 * is already overdue.
 *
 * STOPS WHEN THE JOB REACHES CER
 * ------------------------------
 * The deadline is about reaching the portal, and the JOB is the record that
 * knows whether it did. The report carries only a QA outcome now, so reading
 * it for delivery was how a delivered job kept counting down (BTY7033068).
 */
/** Days from the inspection date to the upload obligation. */
export const UPLOAD_DEADLINE_DAYS = 10;
/** Days from the inspection date at which the dashboard starts warning. */
export const UPLOAD_WARN_AFTER_DAYS = 7;
const DAY_MS = 24 * 60 * 60_000;
/**
 * Job statuses meaning the work reached CER.
 *
 * The office marks the JOB uploaded, and while the legacy portal path is
 * still in use that can happen without the report ever leaving `pending` —
 * so the job is the more reliable answer to "is this delivered?". Reading
 * only the report status made a delivered job count down as though it were
 * outstanding.
 */
const JOB_SETTLED = new Set(['uploaded', 'closed']);
/**
 * How urgent is getting this report uploaded?
 *
 * `inspectionDate` is the inspection date (`YYYY-MM-DD`, or any parseable
 * timestamp); day counting is calendar-based from that date, which is how the
 * obligation is actually expressed and how a person would count it.
 */
export function uploadDeadline(input) {
    // The JOB is the only record that knows whether the work reached CER; the
    // report knows only what QA decided about it.
    if (input.jobStatus !== undefined && JOB_SETTLED.has(input.jobStatus)) {
        return { urgency: 'done' };
    }
    const inspectedMs = parseCalendarDate(input.inspectionDate);
    if (inspectedMs === undefined)
        return { urgency: 'unknown' };
    const today = startOfUtcDay(input.now ?? new Date());
    const daysSinceInspection = Math.floor((today - inspectedMs) / DAY_MS);
    const daysRemaining = UPLOAD_DEADLINE_DAYS - daysSinceInspection;
    const dueDate = new Date(inspectedMs + UPLOAD_DEADLINE_DAYS * DAY_MS).toISOString().slice(0, 10);
    const urgency = daysSinceInspection >= UPLOAD_DEADLINE_DAYS
        ? 'overdue'
        : daysSinceInspection >= UPLOAD_WARN_AFTER_DAYS
            ? 'due-soon'
            : 'ok';
    return { urgency, daysSinceInspection, daysRemaining, dueDate };
}
/**
 * Midnight UTC of the calendar date a timestamp names.
 *
 * Deliberately date-only: the obligation is counted in days, so the hour an
 * inspection started must not make a report tip over a day early or late.
 */
function parseCalendarDate(value) {
    if (!value)
        return undefined;
    const datePart = value.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart))
        return undefined;
    const ms = Date.parse(`${datePart}T00:00:00Z`);
    return Number.isNaN(ms) ? undefined : ms;
}
function startOfUtcDay(now) {
    return Date.parse(`${now.toISOString().slice(0, 10)}T00:00:00Z`);
}
/** Short human label for a row pill. */
export function uploadDeadlineLabel(deadline) {
    switch (deadline.urgency) {
        case 'done':
            return 'Uploaded';
        case 'unknown':
            return 'No inspection date';
        case 'overdue': {
            const over = -(deadline.daysRemaining ?? 0);
            return over === 0 ? 'Due today' : `${over}d overdue`;
        }
        case 'due-soon':
        case 'ok':
            return deadline.daysRemaining === 0 ? 'Due today' : `${deadline.daysRemaining}d left`;
    }
}
//# sourceMappingURL=upload-deadline.js.map